import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AppDispatch } from 'store'
import { CloseIcon } from 'assets/icons/CloseIcon'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { Button } from 'components/ui/Button/Button'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { CostCenterSelect } from 'components/Select/CostCenter.select'
import { Option } from 'components/Select/Select'
import { ISelectValue } from 'constants/globalTypes'
import { createInventoryCounterTransaction } from 'features/inventory/inventoryControlSlice'
import { issueInventoryRequest } from 'features/inventory/inventoryRequestsSlice'
import { parseLocationRegistryOptions } from 'features/locationRegistry/locationRegistryMappers'
import { formatGhs } from 'pages/FuelManagement/Shared/fuelFormatters'
import { CursorPointer, Flex, ItemsGrid } from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import { baseUrl } from 'utils/axios'
import {
    IStoreBasketLine,
    IStoreCounterServe,
    IStoreServeRequestState,
    IStoreServeTabProps,
} from 'types/inventoryCounter.interface'
import { InventoryChargeToKind } from 'types/inventoryEnums.interface'
import {
    CHARGE_KIND_OPTIONS,
    ISSUE_CONTROL_PENDING_CAPTION,
    NATURE_REGISTRY_NOTE,
} from '../Shared/inventoryFormatters'
import {
    BasketLineFields,
    BasketLineIdentity,
    BasketLineName,
    BasketLineRow,
    BasketLineSku,
    BasketLineValue,
    BasketSummaryBar,
    BasketSummaryText,
    NatureNote,
    NatureToggle,
    NatureToggleOption,
    ServeRequestBanner,
    ServeRequestBannerText,
} from './StoreCounter.styled'

const CHARGE_KIND_COST_CENTER = InventoryChargeToKind.CostCenter
const CHARGE_KIND_MACHINE = InventoryChargeToKind.Machine

const parseMachines = (r: any) => r.data.map((m: any) => ({ value: m.id, label: m.name }))

const serveRef = (id: number | string): string => (typeof id === 'number' ? `REQ-${id}` : String(id))

const linesFromServeRequest = (req: IStoreServeRequestState): IStoreBasketLine[] =>
    req.items.map((item, index) => ({
        id: Date.now() + index,
        source: 'REAL',
        item: { value: item.itemId, label: item.name },
        sku: item.sku,
        nature: 'CONSUMABLE',
        quantity: item.quantity,
        unitCostGhs: null,
        chargeToKind: null,
        chargeToLabel: '',
        chargeToRef: null,
        dueBack: null,
        note: '',
    }))

export const ServeTab = ({ onDemoConfirm }: IStoreServeTabProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const routerLocation = useLocation()
    const initialServeRequest =
        (routerLocation.state as { serveRequest?: IStoreServeRequestState } | null)?.serveRequest ?? null
    const [serveRequest, setServeRequest] = useState<IStoreServeRequestState | null>(initialServeRequest)
    const [serve, setServe] = useState<IStoreCounterServe>({
        worker: initialServeRequest?.requesterUser ?? null,
        location: null,
        chargeToKind: null,
        chargeToLabel: '',
        chargeToRef: null,
    })
    const [lines, setLines] = useState<IStoreBasketLine[]>(() =>
        initialServeRequest ? linesFromServeRequest(initialServeRequest) : [],
    )
    const [addKey, setAddKey] = useState(0)
    const [serveKey, setServeKey] = useState(0)

    useEffect(() => {
        let active = true
        baseUrl
            .get('locations', { params: { page: 1, limit: 2 } })
            .then((resp) => {
                if (!active) return
                const options = parseLocationRegistryOptions(resp.data)
                if (options.length === 1) {
                    setServe((prev) => (prev.location ? prev : { ...prev, location: options[0] }))
                }
            })
            .catch(() => undefined)
        return () => {
            active = false
        }
    }, [])

    const handleServeSelect = (value: ISelectValue, name: string) => {
        setServe((prev) => ({ ...prev, [name]: value }))
    }

    const handleChargeKind = (value: ISelectValue) => {
        setServe((prev) => ({ ...prev, chargeToKind: value, chargeToRef: null, chargeToLabel: '' }))
    }

    const handleServeCostCenter = (value: Option) => {
        setServe((prev) => ({
            ...prev,
            chargeToRef: value?.value != null ? { value: Number(value.value), label: value.label } : null,
            chargeToLabel: value?.value != null ? value.label : '',
        }))
    }

    const handleServeMachine = (value: ISelectValue) => {
        setServe((prev) => ({
            ...prev,
            chargeToRef: value?.value ? value : null,
            chargeToLabel: value?.value ? value.label : '',
        }))
    }

    const dismissServeRequest = () => {
        setServeRequest(null)
        navigate(routerLocation.pathname, { replace: true })
    }

    const newLineId = () => Date.now() + lines.length

    const addRealLine = (value: ISelectValue) => {
        if (!value?.value) return
        setLines((prev) => [
            ...prev,
            {
                id: newLineId(),
                source: 'REAL',
                item: value,
                sku: null,
                nature: 'CONSUMABLE',
                quantity: 1,
                unitCostGhs: null,
                chargeToKind: null,
                chargeToLabel: '',
                dueBack: null,
                note: '',
            },
        ])
        setAddKey((prev) => prev + 1)
    }

    const patchLine = (id: number, patch: Partial<IStoreBasketLine>) => {
        setLines((prev) => prev.map((line) => (line.id === id ? { ...line, ...patch } : line)))
    }

    const removeLine = (id: number) => {
        setLines((prev) => prev.filter((line) => line.id !== id))
    }

    const consumables = lines.filter((line) => line.nature === 'CONSUMABLE')
    const returnables = lines.filter((line) => line.nature === 'RETURNABLE')

    const summaryLabel = useMemo(() => {
        if (!lines.length) return null
        const parts: string[] = []
        if (consumables.length) {
            parts.push(`${consumables.length} consumable ${consumables.length === 1 ? 'line' : 'lines'}`)
        }
        if (returnables.length) {
            parts.push(`${returnables.length} ${returnables.length === 1 ? 'returnable' : 'returnables'}`)
        }
        if (consumables.length) {
            parts.push(
                serve.chargeToLabel.trim()
                    ? `charged to ${serve.chargeToLabel.trim()}`
                    : 'charge-to optional',
            )
        }
        return parts.join(' · ')
    }, [lines, serve.chargeToLabel, consumables.length, returnables.length])

    const lineValueLabel = (line: IStoreBasketLine): string =>
        line.unitCostGhs != null && line.quantity > 0 ? formatGhs(line.quantity * line.unitCostGhs) : '—'

    const lineChargeTo = (line: IStoreBasketLine) => {
        const kind = line.chargeToKind?.value != null ? Number(line.chargeToKind.value) : null
        const label = line.chargeToLabel.trim()
        if (kind == null || !label) return undefined
        return {
            kind: kind as InventoryChargeToKind,
            refId: line.chargeToRef?.value != null ? Number(line.chargeToRef.value) : undefined,
            label,
        }
    }

    const headerChargeTo = () => {
        const kind = serve.chargeToKind?.value != null ? Number(serve.chargeToKind.value) : null
        const label = serve.chargeToLabel.trim()
        if (kind == null || !label) return undefined
        return {
            kind: kind as InventoryChargeToKind,
            refId: serve.chargeToRef?.value != null ? Number(serve.chargeToRef.value) : undefined,
            label,
        }
    }

    const handleConfirm = async () => {
        if (!serve.worker?.value) {
            toast.warn('Pick a worker first')
            return
        }
        if (!lines.length) {
            toast.warn('Add at least one line to the basket')
            return
        }
        if (lines.some((line) => !line.quantity || line.quantity <= 0)) {
            toast.warn('Quantity must be above zero on every line')
            return
        }
        if (returnables.some((line) => !line.dueBack)) {
            toast.warn('Due-back date is required on returnable lines')
            return
        }
        if (!serve.location?.value) {
            toast.warn('Pick a location — stock moves need one')
            return
        }

        const resp = await dispatch(
            createInventoryCounterTransaction({
                workerId: Number(serve.worker.value),
                locationId: Number(serve.location.value),
                chargeTo: headerChargeTo(),
                lines: lines.map((line) => ({
                    itemId: Number(line.item.value),
                    quantity: Number(line.quantity),
                    chargeTo: line.nature === 'CONSUMABLE' ? lineChargeTo(line) : undefined,
                    dueBackAt:
                        line.nature === 'RETURNABLE' && line.dueBack
                            ? line.dueBack.toISOString()
                            : undefined,
                    note: line.note.trim() || undefined,
                })),
            }),
        )

        if (resp.meta.requestStatus !== 'fulfilled') return

        onDemoConfirm({ ledgerRows: [], custodyRows: [] })

        const toastParts: string[] = []
        if (consumables.length) {
            toastParts.push(`issued ${consumables.length} ${consumables.length === 1 ? 'line' : 'lines'}`)
        }
        if (returnables.length) {
            const units = returnables.reduce((sum, line) => sum + line.quantity, 0)
            toastParts.push(`assigned ${units} ${units === 1 ? 'tool' : 'tools'}`)
        }
        const sentence = toastParts.join(' · ')
        toast.success(`${sentence.charAt(0).toUpperCase()}${sentence.slice(1)} — ${serve.worker.label}`)

        if (serveRequest) {
            const issueResp = await dispatch(issueInventoryRequest(Number(serveRequest.id)))
            if (issueResp.meta.requestStatus === 'fulfilled') {
                toast.success(`Request ${serveRef(serveRequest.id)} served and marked issued`)
            } else {
                toast.warn(
                    `Goods issued, but request ${serveRef(serveRequest.id)} could not be marked issued`,
                )
            }
            setServeRequest(null)
            navigate(routerLocation.pathname, { replace: true })
        }

        setLines([])
        setServe((prev) => ({ ...prev, worker: null }))
        setServeKey((prev) => prev + 1)
    }

    return (
        <Flex column gap='1rem'>
            {serveRequest && (
                <ServeRequestBanner>
                    <ServeRequestBannerText>
                        {`Serving request ${serveRef(serveRequest.id)} — ${serveRequest.department}`}
                    </ServeRequestBannerText>
                    <CursorPointer onClick={dismissServeRequest}>
                        <CloseIcon />
                    </CursorPointer>
                </ServeRequestBanner>
            )}
            <Container>
                <ContainerHeader>
                    <TitleText>Serve</TitleText>
                </ContainerHeader>
                <ContainerContent gap='1rem'>
                    <ItemsGrid columns={4}>
                        <HonestSelectAsyncInput
                            key={`worker-${serveKey}`}
                            link='users/basic'
                            labelText='Worker'
                            name='worker'
                            onChange={handleServeSelect}
                            parserFunction={(data: any) => data.data.map((i: any) => ({ label: i.fullName, value: i.id }))}
                            defaultValue={serve.worker?.label}
                            nomarg
                            required
                        />
                        <HonestSelectAsyncInput
                            link='locations'
                            placeholder='Search locations'
                            name='location'
                            labelText='Location'
                            defaultValue={serve.location?.label}
                            onChange={handleServeSelect}
                            parserFunction={parseLocationRegistryOptions}
                            nomarg
                        />
                        <HonestSelectInput
                            nomarg
                            labelText='Charge to'
                            name='chargeToKind'
                            options={CHARGE_KIND_OPTIONS}
                            defaultValue={serve.chargeToKind?.value}
                            onChange={handleChargeKind}
                        />
                        {serve.chargeToKind?.value === CHARGE_KIND_COST_CENTER ? (
                            <CostCenterSelect
                                label='Charge to (cost center)'
                                placeholder='Select cost center'
                                width='100%'
                                value={serve.chargeToRef ?? null}
                                onChange={handleServeCostCenter}
                            />
                        ) : serve.chargeToKind?.value === CHARGE_KIND_MACHINE ? (
                            <HonestSelectAsyncInput
                                key={`serve-machine-${serveKey}`}
                                link='machines'
                                labelText='Charge to (machine)'
                                name='chargeToRef'
                                onChange={handleServeMachine}
                                parserFunction={parseMachines}
                                defaultValue={serve.chargeToRef?.label}
                                nomarg
                            />
                        ) : (
                            <Input
                                placeholder={serve.chargeToKind ? 'WO-1042' : 'Maintenance'}
                                labelText={serve.chargeToKind ? 'Work order ref' : 'Charge to (label)'}
                                value={serve.chargeToLabel}
                                type='text'
                                name='chargeToLabel'
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setServe((prev) => ({ ...prev, chargeToLabel: e.target.value }))
                                }
                                nomarg
                            />
                        )}
                    </ItemsGrid>
                    <OtherText grey>{ISSUE_CONTROL_PENDING_CAPTION}</OtherText>
                </ContainerContent>
            </Container>

            <Container>
                <ContainerHeader>
                    <TitleText>Basket</TitleText>
                </ContainerHeader>
                <ContainerContent gap='1rem'>
                    <ItemsGrid columns={1}>
                        <HonestSelectAsyncInput
                            key={`add-real-${addKey}`}
                            name='addItem'
                            labelText='Add item'
                            placeholder='Search stock'
                            nomarg
                            onChange={(v: ISelectValue) => addRealLine(v)}
                            link='inventory/item/stock'
                            additionalParams={{ locationId: serve.location?.value }}
                            parserFunction={(data: any) => data.data.map((i: any) => ({ label: i.name, value: i.id }))}
                        />
                    </ItemsGrid>

                    {!lines.length ? (
                        <OtherText grey>The basket is empty — add an item to start serving.</OtherText>
                    ) : (
                        <Flex column>
                            {lines.map((line) => (
                                <BasketLineRow key={line.id}>
                                    <BasketLineIdentity>
                                        <BasketLineName>{line.item.label}</BasketLineName>
                                        <BasketLineSku>{line.sku || 'No SKU — live item'}</BasketLineSku>
                                        <Flex column gap='0.25rem'>
                                            <NatureToggle>
                                                <NatureToggleOption
                                                    type='button'
                                                    $active={line.nature === 'CONSUMABLE'}
                                                    onClick={() => patchLine(line.id, { nature: 'CONSUMABLE' })}
                                                >
                                                    Consumable
                                                </NatureToggleOption>
                                                <NatureToggleOption
                                                    type='button'
                                                    $active={line.nature === 'RETURNABLE'}
                                                    onClick={() => patchLine(line.id, { nature: 'RETURNABLE' })}
                                                >
                                                    Returnable
                                                </NatureToggleOption>
                                            </NatureToggle>
                                            <NatureNote>{NATURE_REGISTRY_NOTE}</NatureNote>
                                        </Flex>
                                    </BasketLineIdentity>
                                    <BasketLineFields>
                                        <Input
                                            placeholder='1'
                                            labelText='Quantity'
                                            value={line.quantity}
                                            type='number'
                                            name='quantity'
                                            width='6rem'
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                patchLine(line.id, { quantity: e.target.valueAsNumber })
                                            }
                                            nomarg
                                            required
                                            invalid={!line.quantity || line.quantity <= 0}
                                        />
                                        {line.nature === 'CONSUMABLE' ? (
                                            <>
                                                <HonestSelectInput
                                                    nomarg
                                                    labelText='Charge to (line)'
                                                    name='chargeToKind'
                                                    options={CHARGE_KIND_OPTIONS}
                                                    defaultValue={line.chargeToKind?.value}
                                                    onChange={(v: ISelectValue) =>
                                                        patchLine(line.id, { chargeToKind: v, chargeToRef: null, chargeToLabel: '' })
                                                    }
                                                />
                                                {line.chargeToKind?.value === CHARGE_KIND_COST_CENTER ? (
                                                    <CostCenterSelect
                                                        label='Cost center (line)'
                                                        placeholder='Select cost center'
                                                        width='100%'
                                                        value={line.chargeToRef ?? null}
                                                        onChange={(v: Option) =>
                                                            patchLine(line.id, {
                                                                chargeToRef: v?.value != null ? { value: Number(v.value), label: v.label } : null,
                                                                chargeToLabel: v?.value != null ? v.label : '',
                                                            })
                                                        }
                                                    />
                                                ) : line.chargeToKind?.value === CHARGE_KIND_MACHINE ? (
                                                    <HonestSelectAsyncInput
                                                        key={`line-machine-${line.id}`}
                                                        link='machines'
                                                        labelText='Machine (line)'
                                                        name='chargeToRef'
                                                        onChange={(v: ISelectValue) =>
                                                            patchLine(line.id, {
                                                                chargeToRef: v?.value ? v : null,
                                                                chargeToLabel: v?.value ? v.label : '',
                                                            })
                                                        }
                                                        parserFunction={parseMachines}
                                                        defaultValue={line.chargeToRef?.label}
                                                        nomarg
                                                    />
                                                ) : (
                                                    <Input
                                                        placeholder={
                                                            line.chargeToKind
                                                                ? 'WO-1042'
                                                                : serve.chargeToLabel.trim() || 'Basket default'
                                                        }
                                                        labelText={line.chargeToKind ? 'Work order ref (line)' : 'Charge label (line)'}
                                                        value={line.chargeToLabel}
                                                        type='text'
                                                        name='chargeToLabel'
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                            patchLine(line.id, { chargeToLabel: e.target.value })
                                                        }
                                                        nomarg
                                                    />
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <CalendarInput
                                                    labelText='Due back'
                                                    defaultValue={line.dueBack}
                                                    name='dueBack'
                                                    minDate={new Date()}
                                                    onChange={(v: Date) => patchLine(line.id, { dueBack: v })}
                                                    width='100%'
                                                    nomarg
                                                    required
                                                />
                                                <Input
                                                    placeholder='Scratched handle, full kit'
                                                    labelText='Condition / note'
                                                    value={line.note}
                                                    type='text'
                                                    name='note'
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        patchLine(line.id, { note: e.target.value })
                                                    }
                                                    nomarg
                                                />
                                            </>
                                        )}
                                    </BasketLineFields>
                                    <BasketLineValue>{lineValueLabel(line)}</BasketLineValue>
                                    <CursorPointer style={{ paddingTop: '0.75rem' }} onClick={() => removeLine(line.id)}>
                                        <CloseIcon />
                                    </CursorPointer>
                                </BasketLineRow>
                            ))}
                        </Flex>
                    )}

                    {summaryLabel && (
                        <BasketSummaryBar>
                            <BasketSummaryText>{summaryLabel}</BasketSummaryText>
                            <Button variant='primary' onClick={handleConfirm}>
                                Confirm basket
                            </Button>
                        </BasketSummaryBar>
                    )}
                    {!summaryLabel && (
                        <Flex justend>
                            <Button variant='primary' onClick={handleConfirm}>
                                Confirm basket
                            </Button>
                        </Flex>
                    )}
                </ContainerContent>
            </Container>
        </Flex>
    )
}
